/**
 * Commercial pricing copy - customer-facing bands and drivers only.
 * No internal margin, revenue goals, or P&L (see operator-plan.js).
 */
export const pricingContent = {
  intro:
    "Every engagement is scoped after a short discovery call. Figures below are typical starting bands for SMB workloads in Southern California - not quotes until we align on milestones, integrations, and acceptance criteria.",

  offerings: [
    {
      id: "receptionist",
      title: "AI receptionist",
      range: "$4,800 – $14,500+",
      summary:
        "Phone and SMS answering, qualification scripts, bookings, CRM or calendar hooks, failover to your staff.",
      includes: [
        "Channel design (voice/text) and scripted flows tuned to how you operate",
        "Scheduling or CRM touches you approve - no opaque black boxes",
        "Handoffs and escalation rules you define",
      ],
      notIncluded:
        "Per-minute carrier surcharges, new CRM licenses, and custom telephony integrations are quoted separately.",
    },
    {
      id: "ai-chat",
      title: "AI chat (RAG) & private assistants",
      range: "$6,500 – $22,000+",
      summary:
        "Grounded assistants over your uploads, FAQs, tickets, or staff-only data - with access controls you approve.",
      includes: [
        "Knowledge ingestion pipelines and grounding checks",
        "Public site widget or authenticated staff consoles",
        "Review workflow for correcting bad answers safely",
      ],
      notIncluded:
        "Heavy custom model fine-tuning, regulated compliance programs, and unrelated app builds are scoped apart.",
    },
    {
      id: "unifi",
      title: "UniFi network design & installs",
      range: "$2,900 – $18,500+",
      summary:
        "Surveys through commissioning: backbone switching, AP placement, guest nets, VLANs, documentation.",
      includes: [
        "BOM-aligned hardware pass-through billing (transparent)",
        "Tuning visits until coverage meets agreed targets",
        "Hand-off runbooks so your IT or MSP can operate it",
      ],
      notIncluded:
        "Low-voltage licensing in your municipality, trenching/conduit contractors, ISP contracts. We flag requirements early.",
    },
    {
      id: "web",
      title: "Custom sites & launches",
      range: "$5,500 – $25,000+",
      summary:
        "Performance-first landing and marketing sites - or thin app-backed experiences - tracked in git, not fragile themes.",
      includes: [
        "Responsive layouts tailored to brand art direction",
        "Core Web Vitals–aware frontend defaults",
        "Deploy scripts you can rerun or hand to your team",
      ],
      notIncluded:
        "Open-ended redesign rounds, multilingual CMS migration, unrelated product engineering beyond agreed scope.",
    },
  ],

  priceDrivers: [
    "Timeline compression (rush milestones)",
    "Onsite vs remote-heavy work ratios",
    "Number of integrations, environments, or brands in scope",
    "Data residency, retention, or access logging requirements",
    "Hardware BOM size and cabling complexity",
    "Support hours, SLA expectations, after-hours escalation",
    "Training, documentation depth, or train-the-trainer workshops",
  ],

  retainer: {
    headline: "Care & continuity retainers",
    body: "Small teams need predictable upkeep without unlimited liability. Monthly blocks cover monitoring tiers, playbook tweaks, staged content updates for chat/RAG, and network health passes - explicitly capped; overage is time-and-materials with notice.",
    examples: [
      {
        label: "Light caretaker",
        price: "~$795/mo",
        scope:
          "One channel tweak window, quarterly health review, prioritized email triage (business-hours).",
      },
      {
        label: "Active operations",
        price: "~$1,450/mo",
        scope:
          "Bi-weekly checkpoints, prioritized incident response windows, backlog grooming with your stakeholder.",
      },
    ],
    note: "Retainers are Statements of Work addenda under the MSA - they are not all-you-can-request labor.",
  },

  trust: {
    title: "Draft agreement templates",
    body: "Explore sample Master Services Agreement and Digital / AI Implementation SOW language we use as a conversation starter - not a substitute for your counsel reviewing the final exhibits.",
    linkLabel: "View draft templates",
    disclaimerSnippet:
      "Drafts only / not legal advice - see disclaimers on each document.",
  },
};
